'use client';

import { type Dispatch, type RefObject, type SetStateAction, useCallback } from 'react';
import { flushSync } from 'react-dom';

import { getPermalinkEncodedFilterKeys, isPermalinkPath } from '@/features/seo-listing/consts';
import type { SeoResolveData } from '@/features/seo-listing/types';
import { buildFromUrlFromFilters } from '@/features/seo-listing/utils';
import type { FilterAction, FilterState } from '../store';
import {
  buildFilterReloadContext,
  clearListingRestoreSnapshot,
  type FilterReloadContext,
  getBrowserPathname,
  isOnPermalinkRoute,
  type ListingQueryFilters,
  shouldHardResetCatalogNavigation,
  shouldHardResetFandomBreadcrumbNavigation,
} from '../utils';

interface UseListingFilterHandlersParams {
  filterState: FilterState;
  filterStateRef: RefObject<FilterState>;
  dispatch: Dispatch<FilterAction>;
  isOriginals: boolean;
  basePath: string;
  pathname: string;
  debouncedSearchQuery: string;
  beginSeoNavigation: () => void;
  endSeoNavigation: () => void;
  triggerSeoNavigation: (fromUrl: string) => Promise<void>;
  abortSeoNavigation: () => void;
  updateURL: (params: ListingQueryFilters) => void;
  restoreCatalogSeoPage: () => void;
  catalogSeoPageRef: RefObject<SeoResolveData | null>;
  setSelectedCharacterLabel: Dispatch<SetStateAction<string | undefined>>;
  syncRelatedFilterOptions: (context: FilterReloadContext) => void;
}

/**
 * User-facing filter/navigation handlers for the listing grid. Fandom, tag and
 * character changes canonicalize into SEO permalinks via triggerSeoNavigation.
 */
export const useListingFilterHandlers = ({
  filterState,
  filterStateRef,
  dispatch,
  isOriginals,
  basePath,
  pathname,
  debouncedSearchQuery,
  beginSeoNavigation,
  endSeoNavigation,
  triggerSeoNavigation,
  abortSeoNavigation,
  updateURL,
  restoreCatalogSeoPage,
  catalogSeoPageRef,
  setSelectedCharacterLabel,
  syncRelatedFilterOptions,
}: UseListingFilterHandlersParams) => {
  const handleFandomsChange = useCallback(
    (fandoms: string[]) => {
      // Set the guard synchronously, before dispatch — otherwise the
      // intermediate render caused by SET_FANDOMS passes through the updateURL
      // effect before the /api/v1/seo/redirect result is known.
      beginSeoNavigation();
      dispatch({ type: 'SET_FANDOMS', payload: fandoms });

      // Try to canonicalize into an SEO permalink; fanfics data (loadFanfics)
      // is fetched independently by the main effect and is not blocked by this call.
      const fromUrl = buildFromUrlFromFilters(isOriginals ? '/originals' : '/fanfics', {
        fandoms,
        tags: filterState.selectedTags,
        universes: filterState.selectedUniverses,
        statuses: filterState.selectedStatuses,
        ratings: filterState.selectedRatings,
        characters: filterState.selectedCharacters,
        sortBy: filterState.sortBy,
      });
      triggerSeoNavigation(fromUrl);
    },
    [beginSeoNavigation, dispatch, filterState, isOriginals, triggerSeoNavigation],
  );

  const handleTagsChange = useCallback(
    (tags: string[]) => {
      // Tags canonicalize into their own permalink (/fanfics/tags/{slug}),
      // but only when tags is the dimension actually encoded in the current
      // path (we are already on /fanfics/tags/{slug} and switch to another
      // tag), or we are on the base /fanfics. If we are on a permalink of
      // another dimension (fandom/character/pairing —
      // /fanfics/fandoms/{fandom}/characters/{character}), tags is a
      // pass-through GET param like ratings/statuses: it must simply be
      // appended to the query on top of the current path by the regular
      // updateURL effect. Calling triggerSeoNavigation in that case makes the
      // backend return a correct toUrl with ?tags=... preserved, but
      // onFiltersRestored then overwrites filterState from listingGetUrl,
      // which does not include pass-through tags — zeroing the just-selected
      // tag before the fanfics load fires (the fanfics-custom/v2 request goes
      // out without &tags=...).
      const isTagsPathEncoded =
        getPermalinkEncodedFilterKeys(getBrowserPathname()).includes('tags');
      if (isOnPermalinkRoute() && !isTagsPathEncoded) {
        dispatch({ type: 'SET_TAGS', payload: tags });
        return;
      }

      beginSeoNavigation();
      dispatch({ type: 'SET_TAGS', payload: tags });

      const fromUrl = buildFromUrlFromFilters(isOriginals ? '/originals' : '/fanfics', {
        fandoms: filterState.selectedFandoms,
        tags,
        universes: filterState.selectedUniverses,
        statuses: filterState.selectedStatuses,
        ratings: filterState.selectedRatings,
        characters: filterState.selectedCharacters,
        pairingCharacters: filterState.selectedPairingCharacters,
        sortBy: filterState.sortBy,
      });
      triggerSeoNavigation(fromUrl);
    },
    [beginSeoNavigation, dispatch, filterState, isOriginals, triggerSeoNavigation],
  );

  const handleUniversesChange = useCallback(
    (universes: string[]) => {
      dispatch({ type: 'SET_UNIVERSES', payload: universes });
    },
    [dispatch],
  );

  const handleStatusesChange = useCallback(
    (statuses: string[]) => {
      dispatch({ type: 'SET_STATUSES', payload: statuses });
    },
    [dispatch],
  );

  const handleRatingsChange = useCallback(
    (ratings: string[]) => {
      dispatch({ type: 'SET_RATINGS', payload: ratings });
    },
    [dispatch],
  );

  const handleDirectionsChange = useCallback(
    (directions: string[]) => {
      dispatch({ type: 'SET_DIRECTIONS', payload: directions });
    },
    [dispatch],
  );

  const handleCharactersChange = useCallback(
    (characters: string[], selectedLabels?: Record<string, string>) => {
      // Always ask /seo/redirect first: enabled character SEO must land on
      // /fanfics/fandoms/{fandom}/characters/{slug}. When redirect 404s while
      // still on a fandom (or other) permalink whose identity remains selected,
      // onSeoDisabled keeps that permanent URL and onNavigationSettled/updateURL
      // appends ?characters= as pass-through — same end state as tags, but only
      // after giving the character SEO page a chance.
      beginSeoNavigation();
      dispatch({ type: 'SET_CHARACTERS', payload: characters });
      if (characters.length === 1) {
        const label = selectedLabels?.[characters[0]]?.trim();
        if (label) {
          setSelectedCharacterLabel(label);
        }
      } else {
        setSelectedCharacterLabel(undefined);
      }

      const fromUrl = buildFromUrlFromFilters(isOriginals ? '/originals' : '/fanfics', {
        fandoms: filterState.selectedFandoms,
        tags: filterState.selectedTags,
        universes: filterState.selectedUniverses,
        statuses: filterState.selectedStatuses,
        ratings: filterState.selectedRatings,
        characters,
        sortBy: filterState.sortBy,
      });
      triggerSeoNavigation(fromUrl);
    },
    [
      beginSeoNavigation,
      dispatch,
      filterState,
      isOriginals,
      setSelectedCharacterLabel,
      triggerSeoNavigation,
    ],
  );

  const handleCharacterCardNavigate = useCallback(
    (characterId: string, fandomId?: string) => {
      beginSeoNavigation();
      const nextFandoms = fandomId ? [fandomId] : filterState.selectedFandoms;
      dispatch({
        type: 'SYNC_FROM_URL',
        payload: {
          selectedFandoms: nextFandoms,
          selectedCharacters: [characterId],
          selectedPairingCharacters: [],
          currentPage: 1,
        },
      });
      const fromUrl = buildFromUrlFromFilters(isOriginals ? '/originals' : '/fanfics', {
        fandoms: nextFandoms,
        tags: filterState.selectedTags,
        universes: filterState.selectedUniverses,
        statuses: filterState.selectedStatuses,
        ratings: filterState.selectedRatings,
        characters: [characterId],
        sortBy: filterState.sortBy,
      });
      triggerSeoNavigation(fromUrl);
    },
    [beginSeoNavigation, dispatch, filterState, isOriginals, triggerSeoNavigation],
  );

  const handlePageChange = useCallback(
    (page: number) => {
      dispatch({ type: 'SET_CURRENT_PAGE', payload: page });
      // Write the address bar in the click, same as the old buttons.
      // The URL effect can skip this turn (SEO pending / popstate flag),
      // which left ?page unchanged after preventDefault on the <a>.
      updateURL({
        page,
        sort: filterState.sortBy,
        search: debouncedSearchQuery,
        fandoms: filterState.selectedFandoms,
        tags: filterState.selectedTags,
        universes: filterState.selectedUniverses,
        statuses: filterState.selectedStatuses,
        ratings: filterState.selectedRatings,
        characters: filterState.selectedCharacters,
        pairingCharacters: filterState.selectedPairingCharacters,
      });
    },
    [debouncedSearchQuery, dispatch, filterState, updateURL],
  );

  const softResetToCatalog = useCallback((): void => {
    // Drop any in-flight SEO redirect/resolve so it cannot re-apply fandom filters
    // after we clear state (URL→catalog, UI stuck on fandom).
    abortSeoNavigation();
    clearListingRestoreSnapshot();
    beginSeoNavigation();
    flushSync(() => {
      dispatch({
        type: 'SYNC_FROM_URL',
        payload: {
          selectedFandoms: [],
          selectedTags: [],
          selectedUniverses: [],
          selectedStatuses: [],
          selectedRatings: [],
          selectedDirections: [],
          selectedCharacters: [],
          selectedPairingCharacters: [],
          searchQuery: '',
          currentPage: 1,
        },
      });
      restoreCatalogSeoPage();
      setSelectedCharacterLabel(undefined);
    });
    window.history.pushState({}, '', basePath);
    if (!catalogSeoPageRef.current?.title?.trim()) {
      document.title = isOriginals
        ? 'Ориджиналы – читать фф онлайн бесплатно'
        : 'Фанфики – читать фф онлайн бесплатно';
    }
    endSeoNavigation();
    syncRelatedFilterOptions(
      buildFilterReloadContext(basePath, {
        selectedFandoms: [],
        selectedTags: [],
        selectedUniverses: [],
        selectedCharacters: [],
        selectedPairingCharacters: [],
      }),
    );
  }, [
    abortSeoNavigation,
    basePath,
    beginSeoNavigation,
    catalogSeoPageRef,
    dispatch,
    endSeoNavigation,
    isOriginals,
    restoreCatalogSeoPage,
    setSelectedCharacterLabel,
    syncRelatedFilterOptions,
  ]);

  const handleCatalogNavigate = useCallback(
    (catalogHref: string, preventNavigation: () => void): void => {
      const target = (catalogHref.split('?')[0] ?? catalogHref) as string;
      preventNavigation();

      const destination =
        target === basePath || target.startsWith(`${basePath}/`) ? basePath : target;
      const currentFilters = filterStateRef.current;

      if (
        shouldHardResetCatalogNavigation({
          destinationPath: destination,
          basePath,
          browserPathname: getBrowserPathname(),
          browserSearch: typeof window !== 'undefined' ? window.location.search : '',
          selectedFandomsCount: currentFilters.selectedFandoms.length,
          selectedCharactersCount: currentFilters.selectedCharacters.length,
          selectedTagsCount: currentFilters.selectedTags.length,
          selectedPairingCharactersCount: currentFilters.selectedPairingCharacters.length,
          isNextPathPermalink: isPermalinkPath(pathname),
        })
      ) {
        clearListingRestoreSnapshot();
        window.location.assign(destination);
        return;
      }

      softResetToCatalog();
    },
    [basePath, filterStateRef, pathname, softResetToCatalog],
  );

  const handleFandomHrefNavigate = useCallback(
    (href: string, fandomId: string | undefined, preventNavigation: () => void): void => {
      preventNavigation();
      const currentFilters = filterStateRef.current;

      if (
        shouldHardResetFandomBreadcrumbNavigation({
          isNextPathPermalink: isPermalinkPath(pathname),
          browserPathname: getBrowserPathname(),
          browserSearch: typeof window !== 'undefined' ? window.location.search : '',
          selectedCharactersCount: currentFilters.selectedCharacters.length,
          selectedPairingCharactersCount: currentFilters.selectedPairingCharacters.length,
        })
      ) {
        clearListingRestoreSnapshot();
        window.location.assign(href);
        return;
      }

      if (fandomId) {
        handleFandomsChange([fandomId]);
        return;
      }

      window.location.assign(href);
    },
    [filterStateRef, handleFandomsChange, pathname],
  );

  return {
    handleFandomsChange,
    handleTagsChange,
    handleUniversesChange,
    handleStatusesChange,
    handleRatingsChange,
    handleDirectionsChange,
    handleCharactersChange,
    handleCharacterCardNavigate,
    handlePageChange,
    softResetToCatalog,
    handleCatalogNavigate,
    handleFandomHrefNavigate,
  };
};
